import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder, User, FileText } from 'lucide-react';
import { ActivityContextDto, ActivityMessageSegmentDto } from 'api/activity-feed';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { dossierUrlFromContext } from '../utils/activityNavigation';

interface ContextChipProps {
  icon: React.ReactNode;
  label: string;
  url: string | null;
  onNavigate?: () => void;
}

const ContextChip: React.FC<ContextChipProps> = ({ icon, label, url, onNavigate }) => {
  const navigate = useNavigate();

  if (!url) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        {icon}
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onNavigate?.();
        navigate(url);
      }}
      className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-primary hover:underline"
    >
      {icon}
      {label}
    </button>
  );
};

interface ActivityContextChipsProps {
  /** Contexte dossier de l'item — le rendu n'a lieu que si le champ est présent. */
  context?: ActivityContextDto | null;
  /**
   * Segments du message : un chip dont le libellé figure déjà dans le message
   * (nom du client, identifiant de contrat en lien cliquable...) est masqué —
   * pas de doublon d'info dans la même ligne.
   */
  segments?: ActivityMessageSegmentDto[] | null;
  /** Contenu ajouté en fin de ligne (date, courtier...) — affiché même sans contexte. */
  after?: React.ReactNode;
  /** Callback avant navigation (fermer le dropdown, marquer lu...). */
  onNavigate?: () => void;
}

/**
 * Ligne de chips de contexte sous le message riche (flux global et notifications) :
 * 📁 dossier · 👤 client · [branche]. Liens gatés par la matrice (ActivityFeedLinks) —
 * rendus en texte simple sinon. Absent de l'historique d'un dossier (le back n'envoie
 * pas context dans la timeline : pas de répétition du dossier courant).
 */
export const ActivityContextChips: React.FC<ActivityContextChipsProps> = ({
  context,
  segments,
  after,
  onNavigate
}) => {
  const canFollowLinks = useRoleCan(Capability.ActivityFeedLinks);

  // Déjà dit dans le message ? (le libellé du chip apparaît dans un segment)
  const mentionedInMessage = (label: string): boolean =>
    Boolean(segments?.some((s) => s.text && s.text.includes(label)));

  const afterNode = after ? (
    <span className="text-xs text-gray-400">{after}</span>
  ) : null;

  if (!context) {
    return afterNode ? (
      <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        {afterNode}
      </span>
    ) : null;
  }

  const chips: React.ReactNode[] = [];

  if (context.riskIdentifier && !mentionedInMessage(context.riskIdentifier)) {
    chips.push(
      <ContextChip
        key="risk"
        icon={<Folder className="h-3 w-3" />}
        label={context.riskIdentifier}
        url={canFollowLinks ? dossierUrlFromContext(context) : null}
        onNavigate={onNavigate}
      />
    );
  }

  if (context.clientName && !mentionedInMessage(context.clientName)) {
    chips.push(
      <ContextChip
        key="client"
        icon={<User className="h-3 w-3" />}
        label={context.clientName}
        url={canFollowLinks && context.clientId ? `/mes-clients/${context.clientId}` : null}
        onNavigate={onNavigate}
      />
    );
  }

  if (context.contractIdentifier && !mentionedInMessage(context.contractIdentifier)) {
    chips.push(
      <ContextChip
        key="contract"
        icon={<FileText className="h-3 w-3" />}
        label={context.contractIdentifier}
        url={
          canFollowLinks && context.contractId
            ? `/mes-contrats/details/${context.contractId}`
            : null
        }
        onNavigate={onNavigate}
      />
    );
  }

  if (chips.length === 0 && !context.branchCode && !afterNode) return null;

  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {chips.map((chip, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span className="text-xs text-gray-300">·</span>}
          {chip}
        </React.Fragment>
      ))}
      {context.branchCode && (
        <span className="ml-0.5 rounded bg-gray-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {context.branchCode}
        </span>
      )}
      {afterNode && (
        <>
          {(chips.length > 0 || context.branchCode) && (
            <span className="text-xs text-gray-300">·</span>
          )}
          {afterNode}
        </>
      )}
    </span>
  );
};

export default ActivityContextChips;
